import { buildLatestCombinedRecord, getLabRawValue } from './labRecordUtils';
import { STATUS_COLORS, getStatusHex, getWorstStatusColor } from './healthStatusColors';

const toNumericValue = (value) => {
  const parsed = parseFloat(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const hasDisplayValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== '' && String(value).trim() !== '-';

const buildFieldSummary = async ({ rawValue, formula, age, gender, decimals = 0 }) => {
  if (!hasDisplayValue(rawValue)) {
    return {
      value: '-',
      status: 'ไม่มีข้อมูล',
      color: STATUS_COLORS.gray,
      rawValue: null,
    };
  }

  const numericValue = toNumericValue(rawValue);
  if (numericValue === null) {
    const textValue = String(rawValue).trim();
    return {
      value: textValue,
      status: textValue,
      color: STATUS_COLORS.gray,
      rawValue: textValue,
    };
  }

  const formulaResult = formula ? await formula(numericValue, age, gender) : null;
  const color = formulaResult?.color ? getStatusHex(formulaResult.color) : STATUS_COLORS.green;

  return {
    value: `${String(rawValue).trim()} mg/dL`,
    status: formulaResult?.title || 'ปกติ',
    color,
    rawValue: numericValue,
  };
};

const normalizeItems = (items) => {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (Array.isArray(items?.data)) return items.data;
  return [];
};

export const buildLipidStatusSummary = async ({
  cholesterolItems = [],
  hdlItems = [],
  ldlItems = [],
  triglycerideItems = [],
  age = 0,
  gender = '',
  formulas = {},
}) => {
  const latestRecord = buildLatestCombinedRecord(
    [
      { field: 'cholesterol', items: normalizeItems(cholesterolItems) },
      { field: 'hdl', items: normalizeItems(hdlItems) },
      { field: 'ldl', items: normalizeItems(ldlItems) },
      { field: 'triglyceride', items: normalizeItems(triglycerideItems) },
    ],
    getLabRawValue,
  );

  const latestValues = {
    cholesterol: await buildFieldSummary({
      rawValue: latestRecord?.cholesterol,
      formula: formulas.chol,
      age,
      gender,
      decimals: 0,
    }),
    hdl: await buildFieldSummary({
      rawValue: latestRecord?.hdl,
      formula: formulas.hdl,
      age,
      gender,
      decimals: 0,
    }),
    ldl: await buildFieldSummary({
      rawValue: latestRecord?.ldl,
      formula: formulas.ldl,
      age,
      gender,
      decimals: 0,
    }),
    triglyceride: await buildFieldSummary({
      rawValue: latestRecord?.triglyceride,
      formula: formulas.tg,
      age,
      gender,
      decimals: 0,
    }),
  };

  const hasData = Object.values(latestValues).some((item) => hasDisplayValue(item.value));
  const cardColor = hasData
    ? getWorstStatusColor(Object.values(latestValues).map((item) => item.color))
    : STATUS_COLORS.gray;

  return {
    latestRecord,
    latestVisitDate: latestRecord?.rawDate || latestRecord?.date || '',
    latestValues,
    hasData,
    cardColor,
  };
};
